import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { UploadCloud, X, CheckCircle2, MessageCircle, Copy, ChevronLeft } from "lucide-react";

const EVT_LABELS = {
  mens_singles: "Men's Singles",
  mens_doubles: "Men's Doubles",
  womens_singles: "Women's Singles",
  womens_doubles: "Women's Doubles",
  mixed_doubles: "Mixed Doubles",
};
const MAX_MB = 3;

function fileToDataURL(file) {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
}
function ageFromDob(dob) {
  if (!dob) return null;
  try {
    const b = new Date(dob);
    const ref = new Date(new Date().getFullYear(), 11, 31);
    let a = ref.getFullYear() - b.getFullYear();
    if (ref.getMonth() < b.getMonth() || (ref.getMonth() === b.getMonth() && ref.getDate() < b.getDate())) a--;
    return a;
  } catch { return null; }
}
function ageOK(cat, age) {
  if (cat === "19+") return true;   // Open category — everyone eligible
  if (age == null) return false;
  if (cat.startsWith("U-")) return age <= parseInt(cat.slice(2), 10);
  if (cat.endsWith("+")) return age >= parseInt(cat.slice(0, -1), 10);
  return true;
}
function ageLabel(cat) { return cat === "19+" ? "Open (19+)" : cat; }

function ImageDrop({ label, value, onChange, testId }) {
  const uploadRef = useRef();
  const cameraRef = useRef();
  const onFile = async (f) => {
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`Max ${MAX_MB} MB`); return; }
    onChange(await fileToDataURL(f));
  };
  return (
    <div>
      <Label>{label}</Label>
      {value ? (
        <div className="mt-2 relative">
          <img src={value} alt="" className="w-full h-44 object-cover border border-jpa-border rounded-sm" />
          <button type="button" onClick={() => onChange(null)} className="absolute top-1 right-1 bg-white border border-jpa-border p-1 rounded-sm">
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <div className="mt-2 border border-dashed border-jpa-border rounded-sm p-5 flex flex-col items-center gap-3">
          <UploadCloud className="w-6 h-6 text-jpa-muted" />
          <div className="text-xs text-jpa-muted">Max {MAX_MB} MB</div>
          <div className="flex gap-2 w-full">
            <button type="button" onClick={() => cameraRef.current?.click()} data-testid={`${testId}-camera`} className="flex-1 border border-jpa-border rounded-sm py-2 text-xs font-bold uppercase tracking-widest hover:border-jpa-primary hover:text-jpa-primary transition-colors">
              Take photo
            </button>
            <button type="button" onClick={() => uploadRef.current?.click()} data-testid={`${testId}-upload`} className="flex-1 border border-jpa-border rounded-sm py-2 text-xs font-bold uppercase tracking-widest hover:border-jpa-primary hover:text-jpa-primary transition-colors">
              Upload
            </button>
          </div>
        </div>
      )}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
      <input ref={uploadRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
    </div>
  );
}

function StepDots({ step, totalSteps }) {
  const dots = Array.from({ length: totalSteps }, (_, i) => i + 1);
  return (
    <div className="mt-6 flex items-center gap-3" data-testid="qj-step-indicator">
      {dots.map((n, i) => (
        <div key={n} className="flex items-center gap-3">
          <div className={`w-9 h-9 grid place-items-center rounded-full font-bold text-sm ${step === n ? "bg-jpa-primary text-white" : step > n ? "bg-emerald-600 text-white" : "bg-jpa-surface text-jpa-muted border border-jpa-border"}`}>{step > n ? "✓" : n}</div>
          {i < dots.length - 1 && <div className={`w-8 md:w-14 h-px ${step > n ? "bg-emerald-600" : "bg-jpa-border"}`} />}
        </div>
      ))}
    </div>
  );
}

export default function QuickJoinPage() {
  const { id: tid } = useParams();
  const nav = useNavigate();
  const storageKey = `jpa-quick-join:${tid}`;
  const [t, setT] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", whatsapp: "", email: "", gender: "M",
    aadhaar_number: "",
    dob: "",
    jpa_id: "", pgid: "", dupr_id: "",
    tshirt_size: "",
  });
  const [photo, setPhoto] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [showGate, setShowGate] = useState(false);
  const [gateAcknowledged, setGateAcknowledged] = useState(false);
  const [selections, setSelections] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [doublesWarn, setDoublesWarn] = useState(null);
  const [doublesAcked, setDoublesAcked] = useState(false);
  const [restored, setRestored] = useState(false);

  // Add-on flow state
  const [addOnMode, setAddOnMode] = useState(false);
  const [registeredKeys, setRegisteredKeys] = useState([]);
  const [remainingSlots, setRemainingSlots] = useState(null);
  const [addonFee, setAddonFee] = useState(500);
  const [checkedAadhaar, setCheckedAadhaar] = useState("");
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const s = JSON.parse(raw);
        if (s.form) setForm((prev) => ({ ...prev, ...s.form }));
        if (typeof s.step === "number") setStep(s.step);
        if (s.selections) setSelections(s.selections);
        if (s.photo) setPhoto(s.photo);
        if (s.receipt) setReceipt(s.receipt);
        if (s.doublesAcked) setDoublesAcked(true);
        if (s.gateAcknowledged) setGateAcknowledged(true);
      }
    } catch { /* noop */ }
    setRestored(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useEffect(() => {
    if (!restored) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({
        form, step, selections, photo, receipt, doublesAcked, gateAcknowledged,
      }));
    } catch { /* noop */ }
  }, [restored, storageKey, form, step, selections, photo, receipt, doublesAcked, gateAcknowledged]);

  useEffect(() => {
    api.get(`/tournaments/${tid}`).then((r) => {
      if (!r.data.quick_registration) {
        toast.info("This tournament uses the JPA-ID join flow.");
        nav(`/tournaments/${tid}/join`);
        return;
      }
      setT(r.data);
      if (r.data.restrict_to_jharkhand) setShowGate(true);
    }).catch(() => { toast.error("Tournament not found"); nav("/tournaments"); });
  }, [tid, nav]);

  // Aadhaar match check — fires when player types a full 12-digit number.
  useEffect(() => {
    if (!t) return;
    const aad = (form.aadhaar_number || "").trim();
    if (aad.length !== 12) {
      if (addOnMode) {
        // player edited Aadhaar after a match — reset the add-on state
        setAddOnMode(false); setRegisteredKeys([]); setRemainingSlots(null);
      }
      return;
    }
    if (aad === checkedAadhaar) return;
    setCheckedAadhaar(aad);
    setChecking(true);
    api.get(`/tournaments/${tid}/quick-register/check?aadhaar=${aad}`).then((r) => {
      if (r.data.found) {
        const ex = r.data.existing || {};
        setForm((prev) => ({
          ...prev,
          name: ex.name || prev.name,
          whatsapp: ex.whatsapp || prev.whatsapp,
          email: ex.email || prev.email,
          gender: ex.gender || prev.gender,
          dob: ex.dob || prev.dob,
          tshirt_size: ex.tshirt_size || prev.tshirt_size,
          jpa_id: ex.jpa_id || prev.jpa_id,
          pgid: ex.pgid || prev.pgid,
          dupr_id: ex.dupr_id || prev.dupr_id,
        }));
        setRegisteredKeys((r.data.registered_selections || []).map((s) => `${s.event_category}__${s.age_category}`));
        setRemainingSlots(r.data.remaining_slots);
        setAddonFee(r.data.addon_fee || 500);
        setAddOnMode(true);
        toast.success(`Welcome back ${ex.name || ""}! Add-on registration enabled.`);
      } else {
        setAddOnMode(false); setRegisteredKeys([]); setRemainingSlots(null);
      }
    }).catch(() => { /* silent — non-fatal */ })
      .finally(() => setChecking(false));
  }, [t, form.aadhaar_number, tid, checkedAadhaar, addOnMode]);

  const age = useMemo(() => ageFromDob(form.dob), [form.dob]);

  const eventsByAge = useMemo(() => {
    if (!t) return [];
    const events = t.qr_event_categories || [];
    const ages = t.qr_age_categories || [];
    const gender = form.gender;
    const out = [];
    for (const ag of ages) {
      if (form.dob && !ageOK(ag, age)) continue;
      const list = [];
      for (const ev of events) {
        if (ev.startsWith("mens_") && gender !== "M") continue;
        if (ev.startsWith("womens_") && gender !== "F") continue;
        list.push({ event_category: ev, age_category: ag, key: `${ev}__${ag}` });
      }
      if (list.length > 0) out.push({ age_category: ag, events: list });
    }
    return out;
  }, [t, form.gender, form.dob, age]);

  const registeredKeySet = useMemo(() => new Set(registeredKeys), [registeredKeys]);
  const selectedCount = Object.keys(selections).length;
  const maxEvents = t?.qr_max_events || 3;
  const feeTiers = t?.qr_fee_tiers || {};
  const cap = addOnMode ? (remainingSlots || 0) : maxEvents;
  const totalFee = addOnMode ? selectedCount * addonFee : (feeTiers[String(selectedCount)] || 0);
  const upiId = t?.upi_id || "pla1447@fbl";
  const totalSteps = addOnMode ? 2 : 3;
  const noMoreSlots = addOnMode && remainingSlots === 0;

  const toggle = (combo) => {
    if (registeredKeySet.has(combo.key)) return;   // already registered — locked
    if (!selections[combo.key] && combo.event_category.includes("doubles") && !doublesAcked) {
      setDoublesWarn(combo);
      return;
    }
    setSelections((prev) => {
      const next = { ...prev };
      if (next[combo.key]) { delete next[combo.key]; }
      else {
        if (Object.keys(next).length >= cap) {
          toast.error(addOnMode ? `Only ${cap} more event${cap !== 1 ? "s" : ""} allowed` : `Max ${cap} events allowed`);
          return prev;
        }
        next[combo.key] = { event_category: combo.event_category, age_category: combo.age_category, partner_name: "", partner_whatsapp: "", needs_partner: false };
      }
      return next;
    });
  };
  const acknowledgeDoubles = () => {
    setDoublesAcked(true);
    const combo = doublesWarn;
    setDoublesWarn(null);
    if (!combo) return;
    setSelections((prev) => {
      const next = { ...prev };
      if (Object.keys(next).length >= cap) { toast.error(`Only ${cap} more event(s) allowed`); return prev; }
      next[combo.key] = { event_category: combo.event_category, age_category: combo.age_category, partner_name: "", partner_whatsapp: "", needs_partner: false };
      return next;
    });
  };
  const setPartner = (key, field, v) => {
    setSelections((prev) => ({ ...prev, [key]: { ...prev[key], [field]: v } }));
  };
  const toggleNeedsPartner = (key) => {
    setSelections((prev) => {
      const cur = prev[key] || {};
      const needs = !cur.needs_partner;
      return { ...prev, [key]: { ...cur, needs_partner: needs, partner_name: needs ? "" : cur.partner_name, partner_whatsapp: needs ? "" : cur.partner_whatsapp } };
    });
  };

  const copyUpi = async () => {
    try { await navigator.clipboard.writeText(upiId); toast.success("UPI ID copied"); }
    catch { toast.error("Copy failed — please long-press to select"); }
  };

  const goStep2 = () => {
    if (!form.name.trim()) { toast.error("Full name is required"); return; }
    if (form.whatsapp.length !== 10) { toast.error("WhatsApp must be 10 digits"); return; }
    if (!form.email.trim()) { toast.error("Email is required"); return; }
    if (!form.gender) { toast.error("Gender is required"); return; }
    if (!/^\d{12}$/.test((form.aadhaar_number || "").trim())) { toast.error("Aadhaar must be exactly 12 digits"); return; }
    if (checking) { toast.info("Checking your details…"); return; }
    setStep(2); window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const validateStep2 = () => {
    if (!form.dob) { toast.error("Date of Birth is required"); return false; }
    if (selectedCount === 0) { toast.error("Select at least one event"); return false; }
    for (const s of Object.values(selections)) {
      if (s.event_category.includes("doubles") && !s.needs_partner && (!s.partner_name?.trim() || !s.partner_whatsapp?.trim())) {
        toast.error(`Partner details required for ${EVT_LABELS[s.event_category]} (or tick "I need a partner")`); return false;
      }
    }
    if (!receipt) { toast.error("Please upload your payment receipt / screenshot"); return false; }
    return true;
  };
  const goStep3 = () => {
    if (!validateStep2()) return;
    setStep(3); window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const submit = async () => {
    if (!addOnMode) {
      if (!photo) { toast.error("Please upload your photo"); return; }
      if (!form.tshirt_size) { toast.error("Please pick your T-shirt size"); return; }
    }
    setLoading(true);
    const payload = {
      ...form,
      aadhaar_number: (form.aadhaar_number || "").replace(/\s/g, ""),
      photo_data_url: addOnMode ? null : photo,
      receipt_data_url: receipt,
      selections: Object.values(selections),
      add_on: addOnMode,
    };
    try {
      const r = await api.post(`/tournaments/${tid}/quick-register`, payload);
      try { sessionStorage.removeItem(storageKey); } catch { /* noop */ }
      setConfirmation({ ...r.data, tournamentName: t.name, addOn: addOnMode });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && typeof detail === "object" && detail?.reason === "duplicate_aadhaar") {
        toast.error(detail.message || "Already registered", { duration: 6000 });
        if (window.confirm(`${detail.message}\n\nSwitch to "Add More Events" mode now?`)) {
          setAddOnMode(true);
          setStep(1);
        }
      } else if (err.response?.status === 409 && typeof detail === "object" && detail?.reason === "duplicate_whatsapp") {
        if (window.confirm(`${detail.message}\n\nPress OK to register anyway, or Cancel to go back.`)) {
          // retry with a force flag — backend treats aadhaar-blank duplicates as soft
          try {
            const r2 = await api.post(`/tournaments/${tid}/quick-register?force=1`, payload);
            try { sessionStorage.removeItem(storageKey); } catch { /* noop */ }
            setConfirmation({ ...r2.data, tournamentName: t.name, addOn: addOnMode });
            return;
          } catch (err2) {
            toast.error(formatApiErrorDetail(err2.response?.data?.detail) || "Registration failed");
          }
        }
      } else {
        toast.error(formatApiErrorDetail(detail) || "Registration failed");
      }
    } finally { setLoading(false); }
  };
  const submitAddOn = async () => {
    if (!validateStep2()) return;
    submit();
  };

  if (!t) return <div className="max-w-3xl mx-auto p-8 text-jpa-muted">Loading…</div>;

  if (showGate && !gateAcknowledged) {
    return (
      <div data-testid="jharkhand-gate" className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-sm p-6 md:p-8 max-w-md w-full">
          <h2 className="font-display text-2xl md:text-3xl tracking-wider text-jpa-primary mb-3">Jharkhand players only</h2>
          <p className="text-jpa-muted text-sm leading-relaxed">
            This Tournament is Only For Players From Jharkhand. Please Do Not Register If you are not a Player from Jharkhand.
            <br /><br />
            <b>No Refund Of Registration fees will be issued on rejection of registration.</b>
          </p>
          <div className="mt-6 flex gap-2 justify-end">
            <Button data-testid="jharkhand-gate-cancel" variant="outline" className="rounded-sm uppercase tracking-widest text-xs font-bold" onClick={() => nav(`/tournaments/${tid}`)}>Cancel</Button>
            <Button data-testid="jharkhand-gate-agree" className="bg-jpa-primary hover:bg-jpa-primary-hover text-white rounded-sm uppercase tracking-widest text-xs font-bold" onClick={() => setGateAcknowledged(true)}>I understand</Button>
          </div>
        </div>
      </div>
    );
  }

  if (confirmation) {
    return (
      <div data-testid="quick-join-success" className="max-w-2xl mx-auto p-8 md:p-16 text-center">
        <div className="w-20 h-20 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-6">
          <CheckCircle2 className="w-10 h-10 text-emerald-600" />
        </div>
        <h1 className="font-display text-5xl tracking-wider mb-4">{confirmation.addOn ? "ADD-ON RECEIVED" : "REGISTRATION RECEIVED"}</h1>
        <p className="text-jpa-muted text-lg leading-relaxed">
          Thanks <b>{form.name}</b> — we&apos;ve received your {confirmation.addOn ? "additional-event " : ""}registration for <b>{confirmation.tournamentName}</b>. Our team will review your entry and WhatsApp/email you confirmation within 6 hours.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="quick-join-page" className="max-w-3xl mx-auto px-5 py-6 md:p-10">
      <h1 className="font-display text-3xl md:text-5xl tracking-wider leading-tight">{t.name}</h1>

      {t.qr_coordinator_whatsapp && (
        <a
          href={`https://wa.me/${String(t.qr_coordinator_whatsapp).replace(/\D/g, "")}?text=${encodeURIComponent(`Hi, I need help registering for ${t.name}`)}`}
          target="_blank" rel="noreferrer"
          data-testid="quick-join-wa-help"
          className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-sm bg-[#25D366] text-white text-sm font-bold hover:bg-[#1EBE57] transition-colors"
        >
          <MessageCircle className="w-4 h-4" /> Need help? WhatsApp coordinator
        </a>
      )}

      <StepDots step={step} totalSteps={totalSteps} />

      {step === 1 && (
        <section data-testid="qj-step-1" className="mt-8 border border-jpa-border rounded-sm p-5 md:p-6 space-y-6">
          {addOnMode && !noMoreSlots && (
            <div data-testid="qj-addon-banner" className="border-l-4 border-jpa-primary bg-jpa-primary/5 p-4 rounded-sm">
              <div className="text-xs font-bold uppercase tracking-widest text-jpa-primary">Add-on registration</div>
              <div className="text-sm mt-1 leading-relaxed">Welcome back <b>{form.name}</b>! You&apos;ve already registered <b>{registeredKeys.length}</b> event{registeredKeys.length !== 1 ? "s" : ""}. You can add up to <b>{remainingSlots}</b> more event{remainingSlots !== 1 ? "s" : ""} at <b>₹{addonFee}</b> each.</div>
            </div>
          )}
          {addOnMode && noMoreSlots && (
            <div data-testid="qj-no-slots" className="text-center py-8">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 grid place-items-center mb-4"><CheckCircle2 className="w-8 h-8 text-emerald-600" /></div>
              <h2 className="font-display text-3xl tracking-wider mb-2">All events registered</h2>
              <p className="text-jpa-muted leading-relaxed">You&apos;ve already registered all {maxEvents} events for this tournament — no more slots available. See you on match day!</p>
              {t.qr_coordinator_whatsapp && (
                <a
                  href={`https://wa.me/${String(t.qr_coordinator_whatsapp).replace(/\D/g, "")}`}
                  target="_blank" rel="noreferrer"
                  className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-sm bg-[#25D366] text-white text-sm font-bold hover:bg-[#1EBE57]"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp coordinator
                </a>
              )}
            </div>
          )}

          {!noMoreSlots && (
            <>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label>Full name *</Label>
                  <Input data-testid="qj-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} readOnly={addOnMode} disabled={addOnMode} className={`mt-2 rounded-sm h-11 ${addOnMode ? "bg-jpa-surface text-jpa-muted" : ""}`} />
                </div>
                <div>
                  <Label>WhatsApp number * (10 digits)</Label>
                  <Input inputMode="numeric" maxLength={10} data-testid="qj-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="9876543210" readOnly={addOnMode} disabled={addOnMode} className={`mt-2 rounded-sm h-11 ${addOnMode ? "bg-jpa-surface text-jpa-muted" : ""}`} />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" data-testid="qj-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} readOnly={addOnMode} disabled={addOnMode} className={`mt-2 rounded-sm h-11 ${addOnMode ? "bg-jpa-surface text-jpa-muted" : ""}`} />
                </div>
                <div>
                  <Label>Gender *</Label>
                  <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })} disabled={addOnMode}>
                    <SelectTrigger data-testid="qj-gender" className={`mt-2 rounded-sm h-11 ${addOnMode ? "bg-jpa-surface text-jpa-muted" : ""}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="M">Male</SelectItem><SelectItem value="F">Female</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>Aadhaar number * (12 digits)</Label>
                  <Input
                    inputMode="numeric"
                    maxLength={12}
                    data-testid="qj-aadhaar-number"
                    value={form.aadhaar_number}
                    onChange={(e) => setForm({ ...form, aadhaar_number: e.target.value.replace(/\D/g, "").slice(0, 12) })}
                    placeholder="XXXX XXXX XXXX"
                    className="mt-2 rounded-sm h-11 font-mono tracking-widest"
                  />
                  <div className="text-[11px] text-jpa-muted mt-1.5">
                    {checking ? "Checking…" : addOnMode ? "Matched with your earlier registration." : "Only the 12-digit number is required. Photo/scan is not needed."}
                  </div>
                </div>
              </div>
              <div className="flex justify-end pt-1">
                <Button type="button" onClick={goStep2} data-testid="qj-step1-next" className="bg-jpa-primary hover:bg-jpa-primary-hover text-white font-bold uppercase tracking-widest rounded-sm h-12 px-8 w-full md:w-auto">
                  Save & continue
                </Button>
              </div>
            </>
          )}
        </section>
      )}

      {step === 2 && (
        <section data-testid="qj-step-2" className="mt-8 border border-jpa-border rounded-sm p-5 md:p-6 space-y-7">
          {addOnMode && (
            <div data-testid="qj-addon-banner-2" className="border-l-4 border-jpa-primary bg-jpa-primary/5 p-3 rounded-sm text-xs">
              Add-on registration — <b>₹{addonFee}</b> per extra event · you have <b>{remainingSlots}</b> slot{remainingSlots !== 1 ? "s" : ""} left
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label>Date of Birth *</Label>
              <Input type="date" data-testid="qj-dob" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} readOnly={addOnMode} disabled={addOnMode} className={`mt-2 rounded-sm h-11 ${addOnMode ? "bg-jpa-surface text-jpa-muted" : ""}`} />
              {age != null && <div className="text-[11px] text-jpa-muted mt-1.5">Age on 31-Dec {new Date().getFullYear()}: <b>{age}</b></div>}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">JPA ID (opt)</Label><Input data-testid="qj-jpa" value={form.jpa_id} onChange={(e) => setForm({ ...form, jpa_id: e.target.value })} className="mt-2 rounded-sm h-10" /></div>
              <div><Label className="text-xs">PG ID (opt)</Label><Input data-testid="qj-pgid" value={form.pgid} onChange={(e) => setForm({ ...form, pgid: e.target.value })} className="mt-2 rounded-sm h-10" /></div>
              <div><Label className="text-xs">DUPR ID (opt)</Label><Input data-testid="qj-dupr" value={form.dupr_id} onChange={(e) => setForm({ ...form, dupr_id: e.target.value })} className="mt-2 rounded-sm h-10" /></div>
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="font-display text-xl tracking-wider">Events</h3>
              <span className="text-xs text-jpa-muted">{addOnMode ? <>up to <b>{remainingSlots}</b> more · picked <b>{selectedCount}</b></> : <>up to <b>{cap}</b> · picked <b>{selectedCount}</b></>}</span>
            </div>
            {!form.dob ? (
              <div className="border border-dashed border-jpa-border p-6 text-center text-sm text-jpa-muted rounded-sm">Enter your DOB above to see eligible events.</div>
            ) : eventsByAge.length === 0 ? (
              <div className="border border-dashed border-jpa-border p-6 text-center text-sm text-jpa-muted rounded-sm">No age-eligible categories in this tournament for your DOB/gender.</div>
            ) : (
              <div className="space-y-4">
                {eventsByAge.map((group) => (
                  <div key={group.age_category} data-testid={`qj-age-group-${group.age_category}`} className="border border-jpa-border rounded-sm overflow-hidden">
                    <div className="bg-jpa-surface px-4 py-2 border-b border-jpa-border">
                      <div className="text-xs font-bold uppercase tracking-widest text-jpa-primary">{ageLabel(group.age_category)}</div>
                    </div>
                    <div className="divide-y divide-jpa-border">
                      {group.events.map((c) => {
                        const alreadyRegistered = registeredKeySet.has(c.key);
                        const chosen = !!selections[c.key];
                        const isDoubles = c.event_category.includes("doubles");
                        return (
                          <div key={c.key} className={alreadyRegistered ? "bg-emerald-50/60" : chosen ? "bg-jpa-primary/5" : ""}>
                            <label className={`flex items-center gap-3 px-4 py-3 ${alreadyRegistered ? "cursor-not-allowed opacity-80" : "cursor-pointer"}`}>
                              <input type="checkbox" checked={alreadyRegistered || chosen} disabled={alreadyRegistered} onChange={() => toggle(c)} data-testid={`qj-evt-${c.key}`} className="w-4 h-4 accent-jpa-primary" />
                              <div className="text-sm font-bold flex-1">{EVT_LABELS[c.event_category]}</div>
                              {alreadyRegistered && <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-sm">Already registered</span>}
                            </label>
                            {chosen && !alreadyRegistered && isDoubles && (
                              <div className="px-4 pb-4 pt-1 pl-11 space-y-2">
                                <label className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-jpa-primary cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={!!selections[c.key].needs_partner}
                                    onChange={() => toggleNeedsPartner(c.key)}
                                    data-testid={`qj-needs-partner-${c.key}`}
                                    className="w-4 h-4 accent-jpa-primary"
                                  />
                                  I need a partner — please match me
                                </label>
                                {selections[c.key].needs_partner ? (
                                  <div className="text-[11px] text-jpa-muted italic">Admin will pair you with another player who also needs a partner.</div>
                                ) : (
                                  <>
                                    <Input placeholder="Partner name" data-testid={`qj-partner-name-${c.key}`} value={selections[c.key].partner_name || ""} onChange={(e) => setPartner(c.key, "partner_name", e.target.value)} className="rounded-sm h-9 text-sm" />
                                    <Input inputMode="numeric" maxLength={10} placeholder="Partner WhatsApp (10 digits)" data-testid={`qj-partner-wa-${c.key}`} value={selections[c.key].partner_whatsapp || ""} onChange={(e) => setPartner(c.key, "partner_whatsapp", e.target.value.replace(/\D/g, "").slice(0, 10))} className="rounded-sm h-9 text-sm" />
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {selectedCount > 0 && (
              <div className="mt-5 p-4 bg-jpa-primary text-white rounded-sm flex items-center justify-between">
                <span className="font-bold uppercase tracking-widest text-sm">
                  {addOnMode ? `Add-on fee (${selectedCount} × ₹${addonFee})` : `Total fee (${selectedCount} event${selectedCount > 1 ? "s" : ""})`}
                </span>
                <span className="font-display text-3xl">₹{totalFee}</span>
              </div>
            )}
          </div>

          {selectedCount > 0 && (
            <div data-testid="qj-payment-section" className="border-2 border-jpa-primary rounded-sm p-5 bg-jpa-primary/5">
              <h3 className="font-display text-xl tracking-wider mb-3 text-jpa-primary">Payment</h3>
              <div className="grid md:grid-cols-2 gap-5 items-start">
                <div>
                  {t.payment_qr_data_url ? (
                    <div className="border border-jpa-border rounded-sm p-3 bg-white">
                      <div className="text-xs font-bold uppercase tracking-widest text-jpa-primary mb-2">Scan to pay ₹{totalFee}</div>
                      <img src={t.payment_qr_data_url} alt="Payment QR" data-testid="qj-payment-qr" className="w-full max-w-[280px] h-auto object-contain mx-auto" />
                      <div className="text-[11px] text-jpa-muted mt-2 text-center">Open any UPI app → Scan → Enter <b>₹{totalFee}</b></div>
                    </div>
                  ) : (
                    <div className="border border-dashed border-jpa-border rounded-sm p-6 text-sm text-jpa-muted">
                      Payment QR not uploaded yet. Please WhatsApp the coordinator for payment details.
                    </div>
                  )}
                  <div className="mt-3 p-3 border border-jpa-border rounded-sm bg-white">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-muted">UPI ID (also works)</div>
                    <div className="mt-1 flex items-center gap-2">
                      <code data-testid="qj-upi-id" className="text-sm font-mono font-bold flex-1 truncate">{upiId}</code>
                      <Button type="button" size="sm" variant="outline" data-testid="qj-copy-upi" onClick={copyUpi} className="rounded-sm text-xs uppercase tracking-widest font-bold gap-1">
                        <Copy className="w-3 h-3" /> Copy
                      </Button>
                    </div>
                  </div>
                </div>
                <div>
                  <ImageDrop label="Payment Receipt / Screenshot *" value={receipt} onChange={setReceipt} testId="qj-receipt" />
                  <div className="text-[11px] text-jpa-muted mt-2">Pay ₹{totalFee} using the QR or UPI ID, then upload the screenshot.</div>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 pt-1">
            <Button type="button" variant="outline" data-testid="qj-step2-back" onClick={() => setStep(1)} className="rounded-sm uppercase tracking-widest text-xs font-bold gap-1 h-12">
              <ChevronLeft className="w-4 h-4" /> Back
            </Button>
            {addOnMode ? (
              <Button disabled={loading} type="button" onClick={submitAddOn} data-testid="qj-step2-submit" className="bg-jpa-primary hover:bg-jpa-primary-hover text-white font-bold uppercase tracking-widest rounded-sm h-12 px-8 flex-1 md:flex-none">
                {loading ? "Submitting…" : "Submit"}
              </Button>
            ) : (
              <Button type="button" onClick={goStep3} data-testid="qj-step2-next" className="bg-jpa-primary hover:bg-jpa-primary-hover text-white font-bold uppercase tracking-widest rounded-sm h-12 px-8 flex-1 md:flex-none">
                Save & continue
              </Button>
            )}
          </div>
        </section>
      )}

      {step === 3 && !addOnMode && (
        <section data-testid="qj-step-3" className="mt-8 border border-jpa-border rounded-sm p-5 md:p-6 space-y-6">
          <ImageDrop label="Player photo *" value={photo} onChange={setPhoto} testId="qj-photo" />
          <div>
            <Label>T-shirt size *</Label>
            <Select value={form.tshirt_size} onValueChange={(v) => setForm({ ...form, tshirt_size: v })}>
              <SelectTrigger data-testid="qj-tshirt" className="mt-2 rounded-sm h-11"><SelectValue placeholder="Pick size" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Small">Small</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Large">Large</SelectItem>
                <SelectItem value="XL">XL</SelectItem>
                <SelectItem value="XXL">XXL</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between gap-3 pt-1">
            <Button type="button" variant="outline" data-testid="qj-step3-back" onClick={() => setStep(2)} className="rounded-sm uppercase tracking-widest text-xs font-bold gap-1 h-12">
              <ChevronLeft className="w-4 h-4" /> Back
            </Button>
            <Button disabled={loading} type="button" onClick={submit} data-testid="qj-submit" className="bg-jpa-primary hover:bg-jpa-primary-hover text-white font-bold uppercase tracking-widest rounded-sm h-12 px-8 flex-1 md:flex-none">
              {loading ? "Submitting…" : "Submit"}
            </Button>
          </div>
        </section>
      )}

      {doublesWarn && (
        <div data-testid="doubles-warn" className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full relative">
            <button
              type="button"
              onClick={() => setDoublesWarn(null)}
              data-testid="doubles-warn-close"
              aria-label="Close"
              className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-sm hover:bg-jpa-surface"
            >
              <X className="w-4 h-4" />
            </button>
            <h2 className="font-display text-2xl tracking-wider text-jpa-primary mb-3 pr-8">Doubles event</h2>
            <p className="text-jpa-muted text-sm leading-relaxed">
              You are Choosing a Doubles Event, Your Partner Need to Register Separately and Pay Separate Registration Fees.
              <br /><br />
              <b>No Refund Will be issued, If your Partner fail to register.</b>
            </p>
            <div className="mt-6 flex justify-end">
              <Button
                data-testid="doubles-warn-ack"
                onClick={acknowledgeDoubles}
                className="bg-jpa-primary hover:bg-jpa-primary-hover text-white rounded-sm uppercase tracking-widest text-xs font-bold"
              >
                Ok, I understand
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
